import { apiRequests } from '../utilities/axios-factory'
import store from '../store'
import { generateKeywordsQueryForOr } from '@smarttransit/common'

function getArchivedOrUnarchivedPassenger ({ id, filter }) {
  filter = {
    include: [
      {
        relation: 'passengerIdentifiers',
        scope: {
          limit: 1,
          order: ['dateUpdated DESC NULLS LAST', 'dateCreated DESC'],
          include: ['mobileMoneyAccounts']
        }
      },
      {
        relation: 'passengerTrips',
        scope: {
          limit: 1,
          order: 'dateUpdated DESC'
        }
      },
      {
        relation: 'passengerAlternateIdentifiers',
        scope: {
          order: 'dateUpdated DESC'
        }
      },
      'stUser'
    ],
    ...filter
  }

  return apiRequests.get('/v1/raw-passenger-profile/' + id + '/', {
    params: { filter },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function getPassengerIdentifier ({ id, filter }) {
  return apiRequests.get('/v1/passenger-identifier/' + id + '/', {
    params: { filter },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function findPassengerIdentifiers (filter) {
  return apiRequests.get('/v1/passenger-identifier/join/', {
    params: { filter },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function findTotalPassengers ({ where, join }) {
  return apiRequests.get('/v1/passenger-identifier/count/join/', {
    params: { where, join },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

function findPassengerIdentifiersByPassengerProfileId (passengerProfileId) {
  return apiRequests.get('/v1/passenger-identifier/join/', {
    params: { filter: { where: { passengerProfileId } } },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @param {number} [limit]
 * @param {number} [offset]
 * @param {string|string[]} [order=['dateCreated DESC', 'dateUpdated DESC']]
 * @returns {Promise<*>}
 */
function findVerifiedPassengers ({
  keywords,
  identifierType,
  countryId,
  limit,
  offset,
  order = ['dateUpdated DESC NULLS LAST', 'dateCreated DESC']
}) {
  const filter = {
    join: {
      relation: 'passengerIdentifiers',
      scope: { where: { and: [{ verified: true }, { phoneNumber: { nilike: '%deleted%' } }] } }
    },
    include: [
      {
        relation: 'passengerIdentifiers',
        scope: {
          limit: 1,
          order: ['dateUpdated DESC NULLS LAST', 'dateCreated DESC']
        }
      },
      {
        relation: 'passengerTrips',
        scope: {
          limit: 1,
          order: 'dateUpdated DESC'
        }
      },
      {
        relation: 'passengerAlternateIdentifiers',
        scope: { order: ['dateUpdated DESC'] }
      }
    ],
    limit,
    offset,
    order
  }

  if (countryId) {
    filter.where = { and: [{ or: [{ currentCountryIdDetected: countryId }, { currentCountryIdSet: countryId }] }] }
  }

  if (identifierType) {
    filter.join.scope.where.and.push({ identifierType })
    filter.include[0].scope.where = { ...filter.join.scope.where }
  }

  if (keywords) {
    if (/^\+{0,1}[0-9\s]+$/u.test(keywords)) {
      filter.join.scope.where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
      filter.include[0].scope.where = { ...filter.join.scope.where }
    } else {
      filter.include[0].scope.where = { ...filter.join.scope.where }

      filter.join = [
        { ...filter.join },
        {
          relation: 'passengerAlternateIdentifiers',
          scope: {
            where: {
              and: [
                { dateArchived: null },
                { or: generateKeywordsQueryForOr(keywords, ['identifierKey']) }
              ]
            }
          }
        }
      ]
    }
  }

  return apiRequests.get('/v1/passenger-profile/join/', {
    params: { filter },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @returns {Promise<*>}
 */
function findTotalVerifiedPassengers ({ keywords, identifierType, countryId }) {
  let where

  let join = {
    relation: 'passengerIdentifiers',
    scope: { where: { and: [{ verified: true }, { phoneNumber: { nilike: '%deleted%' } }] } }
  }

  if (countryId) {
    where = { and: [{ or: [{ currentCountryIdDetected: countryId }, { currentCountryIdSet: countryId }] }] }
  }

  if (identifierType) {
    join.scope.where.and.push({ identifierType })
  }

  if (keywords) {
    if (/^\+{0,1}[0-9\s]+$/u.test(keywords)) {
      join.scope.where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
    } else {
      join = [
        { ...join },
        {
          relation: 'passengerAlternateIdentifiers',
          scope: {
            where: {
              and: [
                { dateArchived: null },
                { or: generateKeywordsQueryForOr(keywords, ['identifierKey']) }
              ]
            }
          }
        }
      ]
    }
  }

  return apiRequests.get('/v1/passenger-profile/count/join/', {
    params: { where, join },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @param {number} [limit]
 * @param {number} [offset]
 * @param {string|string[]} [order=['dateCreated DESC', 'dateUpdated DESC']]
 * @returns {Promise<*>}
 */
function findPendingPassengers ({
  keywords,
  identifierType,
  countryId,
  limit,
  offset,
  order = ['dateUpdated DESC NULLS LAST', 'dateCreated DESC']
}) {
  const filter = {
    where: {
      and: [
        { phoneNumber: { nilike: '%deleted%' } },
        { activationCodeExpiry: { neq: null } },
        {
          or: [
            { activationCode: { neq: null } },
            { activationCode: { neq: '' } }
          ]
        }
      ]
    },
    include: [{
      relation: 'passengerProfile',
      scope: {
        relation: 'passengerAlternateIdentifiers',
        scope: {
          order: ['dateUpdated DESC']
        }
      }
    }],
    limit,
    offset,
    order
  }

  if (countryId) {
    filter.include[0].scope.where = { or: [{ currentCountryIdDetected: countryId }, { currentCountryIdSet: countryId }] }
  }

  if (identifierType) {
    filter.where.and.push({ identifierType })
    // filter.include[0].scope.where = { ...filter.join.scope.where }
  }

  if (keywords && /^\+{0,1}[0-9\s]+$/u.test(keywords)) {
    filter.where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
    // filter.include[0].scope.where = { ...filter.join.scope.where }
  }

  return apiRequests.get('/v1/passenger-identifier/', {
    params: { filter },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @returns {Promise<*>}
 */
function findTotalPendingPassengers ({ keywords, identifierType, countryId }) {
  let where = {
    and: [
      { phoneNumber: { nilike: '%deleted%' } },
      { activationCodeExpiry: { neq: null } },
      {
        or: [
          { activationCode: { neq: null } },
          { activationCode: { neq: '' } }
        ]
      }
    ]
  }

  if (identifierType) {
    where.and.push({ identifierType })
  }

  if (keywords && /^\+{0,1}[0-9\s]+$/u.test(keywords)) {
    where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
  }

  return apiRequests.get('/v1/passenger-identifier/count/', {
    params: { where },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @param {number} [limit]
 * @param {number} [offset]
 * @param {string|string[]} [order=['dateCreated DESC', 'dateUpdated DESC']]
 * @returns {Promise<*>}
 */
function findPendingTransferPassengers ({
  keywords,
  identifierType,
  countryId,
  limit,
  offset,
  order = ['dateUpdated DESC NULLS LAST', 'dateCreated DESC']
}) {
  const filter = {
    join: {
      relation: 'passengerIdentifiers',
      scope: {
        where: {
          and: [
            { phoneNumber: { nilike: '%deleted%' } },
            { transferCodeExpiry: { neq: null } },
            {
              or: [
                { transferCode: { neq: null } },
                { transferCode: { neq: '' } }
              ]
            }
          ]
        }
      }
    },
    include: [
      { relation: 'passengerIdentifiers', scope: { limit: 1, order: ['dateUpdated DESC', 'dateCreated DESC'] } },
      { relation: 'passengerAlternateIdentifiers', scope: { order: ['dateUpdated DESC'] } }
    ],
    limit,
    offset,
    order
  }

  if (countryId) {
    filter.where = { and: [{ or: [{ currentCountryIdDetected: countryId }, { currentCountryIdSet: countryId }] }] }
  }

  if (identifierType) {
    filter.join.scope.where.and.push({ identifierType })
    filter.include[0].scope.where = { ...filter.join.scope.where }
  }

  if (keywords) {
    if (/^\+{0,1}[0-9\s]+$/u.test(keywords)) {
      filter.join.scope.where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
      filter.include[0].scope.where = { ...filter.join.scope.where }
    } else {
      filter.include[0].scope.where = { ...filter.join.scope.where }

      filter.join = [
        { ...filter.join },
        {
          relation: 'passengerAlternateIdentifiers',
          scope: {
            where: {
              and: [
                { dateArchived: null },
                { or: generateKeywordsQueryForOr(keywords, ['identifierKey']) }
              ]
            }
          }
        }
      ]
    }
  }

  return apiRequests.get('/v1/passenger-profile/join/', {
    params: { filter },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @returns {Promise<*>}
 */
function findTotalPendingTransferPassengers ({ keywords, identifierType, countryId }) {
  let where

  let join = {
    relation: 'passengerIdentifiers',
    scope: {
      where: {
        and: [
          { phoneNumber: { nilike: '%deleted%' } },
          { transferCodeExpiry: { neq: null } },
          {
            or: [
              { transferCode: { neq: null } },
              { transferCode: { neq: '' } }
            ]
          }
        ]
      }
    }
  }

  if (countryId) {
    where = { and: [{ or: [{ currentCountryIdDetected: countryId }, { currentCountryIdSet: countryId }] }] }
  }

  if (identifierType) {
    join.scope.where.and.push({ identifierType })
  }

  if (keywords) {
    if (/^\+{0,1}[0-9\s]+$/u.test(keywords)) {
      join.scope.where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
    } else {
      join = [
        { ...join },
        {
          relation: 'passengerAlternateIdentifiers',
          scope: {
            where: {
              and: [
                { dateArchived: null },
                { or: generateKeywordsQueryForOr(keywords, ['identifierKey']) }
              ]
            }
          }
        }
      ]
    }
  }

  return apiRequests.get('/v1/passenger-profile/count/join/', {
    params: { where, join },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @param {number} [limit]
 * @param {number} [offset]
 * @param {string|string[]} [order=['dateCreated DESC', 'dateUpdated DESC']]
 * @returns {Promise<*>}
 */
function findArchivedPassengers ({
  keywords,
  identifierType,
  countryId,
  limit,
  offset,
  order = ['dateUpdated DESC NULLS LAST', 'dateCreated DESC']
}) {
  const filter = {
    where: { and: [{ dateArchived: { neq: null } }] },
    include: [
      {
        relation: 'passengerIdentifiers',
        scope: { limit: 1, order: 'dateUpdated DESC NULLS LAST' }
      }, {
        relation: 'passengerTrips',
        scope: { limit: 1, order: 'dateUpdated DESC' }
      },
      {
        relation: 'passengerAlternateIdentifiers',
        scope: { order: 'dateUpdated DESC' }
      }],
    limit,
    offset,
    order
  }

  if (countryId) {
    filter.where.and.push({ or: [{ currentCountryIdDetected: countryId }, { currentCountryIdSet: countryId }] })
  }

  if (identifierType) {
    if (!filter.join) {
      filter.join = {
        relation: 'passengerIdentifiers',
        scope: {
          where: { and: [] }
        }
      }
    }

    filter.join.scope.where.and.push({ identifierType })
    filter.include[0].scope.where = { ...filter.join.scope.where }
  }

  if (keywords) {
    if (/^\+{0,1}[0-9\s]+$/u.test(keywords)) {
      if (!filter.join) {
        filter.join = {
          relation: 'passengerIdentifiers',
          scope: {
            where: { and: [] }
          }
        }
      }

      filter.join.scope.where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
      filter.include[0].scope.where = { ...filter.join.scope.where }
    } else {
      filter.join = [
        (filter.join ? { ...filter.join } : null),
        {
          relation: 'passengerAlternateIdentifiers',
          scope: {
            where: { or: generateKeywordsQueryForOr(keywords, ['identifierKey']) }
          }
        }
      ].filter(Boolean)
    }
  }

  return apiRequests.get('/v1/raw-passenger-profile/join/', {
    params: { filter },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 *
 * @param {string} [keywords]
 * @param {string} [identifierType]
 * @param {string} [countryId]
 * @returns {Promise<*>}
 */
function findTotalArchivedPassengers ({ keywords, identifierType, countryId }) {
  const where = { and: [{ dateArchived: { neq: null } }] }

  let join

  if (countryId) {
    where.and.push({ or: [{ currentCountryIdDetected: countryId }, { currentCountryIdSet: countryId }] })
  }

  if (identifierType) {
    if (!join) {
      join = {
        relation: 'passengerIdentifiers',
        scope: {
          where: { and: [] }
        }
      }
    }

    join.scope.where.and.push({ identifierType })
  }

  if (keywords) {
    if (/^\+{0,1}[0-9\s]+$/u.test(keywords)) {
      if (!join) {
        join = {
          relation: 'passengerIdentifiers',
          scope: {
            where: { and: [] }
          }
        }
      }

      join.scope.where.and.push({ or: generateKeywordsQueryForOr(keywords, ['phoneNumber']) })
    } else {
      join = [
        (join ? { ...join } : null),
        {
          relation: 'passengerAlternateIdentifiers',
          scope: {
            where: { or: generateKeywordsQueryForOr(keywords, ['identifierKey']) }
          }
        }
      ].filter(Boolean)
    }
  }

  return apiRequests.get('/v1/raw-passenger-profile/count/join/', {
    params: { where, join },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function archivePassenger (passengerProfileId) {
  return apiRequests.delete('/v1/passenger-profile/' + passengerProfileId + '/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function sendNewPassengerPassword (passengerProfileId) {
  return apiRequests.post('/v1/passenger-profile/' + passengerProfileId + '/temporary-password/', {}, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function clearPassengerReRegistrationGuard (passengerIdentifierId) {
  return apiRequests.delete('/v1/passenger-identifier/' + passengerIdentifierId + '/re-registration-guard/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function unarchivePassenger ({ passengerProfileId, passengerIdentifierId }) {
  return apiRequests.patch('/v1/passenger-profile/' + passengerProfileId + '/unarchive/', { passengerIdentifierId }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function refreshMobileMoneyProfile (passengerProfileId) {
  return apiRequests.patch('/v1/passenger-profile/' + passengerProfileId + '/mobile-money-profile/', {}, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function findBulkPassengers ({
  keywords,
  dateCategory,
  dateFrom,
  dateTo,
  countryId,
  passengerListCategory,
  walletBalance,
  ticketsPurchased,
  ticketPurchaseFrequency,
  daysSinceLastTrip,
  limit,
  offset,
  order,
  includePassengerListData
}) {
  const frequencyParams = ticketPurchaseFrequency
    ? {
      minPurchasesPerWeek: ticketPurchaseFrequency.minPurchasesPerWeek,
      evaluationWeeks: ticketPurchaseFrequency.evaluationWeeks
    }
    : {}

  return apiRequests.get('/v1/passenger-profile/search/', {
    params: {
      keywords,
      dateCategory,
      dateFrom,
      dateTo,
      countryId,
      passengerListCategory,
      walletBalance,
      ticketsPurchased,
      ...frequencyParams,
      daysSinceLastTrip,
      limit,
      offset,
      order,
      includePassengerListData
    },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function findTotalBulkPassengers ({
  keywords,
  dateCategory,
  dateFrom,
  dateTo,
  countryId,
  passengerListCategory,
  walletBalance,
  ticketsPurchased,
  ticketPurchaseFrequency,
  daysSinceLastTrip
}) {
  const frequencyParams = ticketPurchaseFrequency
    ? {
      minPurchasesPerWeek: ticketPurchaseFrequency.minPurchasesPerWeek,
      evaluationWeeks: ticketPurchaseFrequency.evaluationWeeks
    }
    : {}

  return apiRequests.get('/v1/passenger-profile/search/count/', {
    params: {
      keywords,
      dateCategory,
      dateFrom,
      dateTo,
      countryId,
      passengerListCategory,
      walletBalance,
      ticketsPurchased,
      ...frequencyParams,
      daysSinceLastTrip
    },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function sendPassengersBulkNotification ({
  useBackupSMS,
  adhocPhoneNumbers,
  passengerProfileIds,
  smsContent,
  keywords,
  dateCategory,
  dateFrom,
  dateTo,
  countryId,
  walletBalance,
  ticketsPurchased,
  ticketPurchaseFrequency,
  daysSinceLastTrip
}) {
  return apiRequests.post('/v1/passenger-profile/bulk-notification/', {
    useBackupSMS,
    adhocPhoneNumbers,
    passengerProfileIds,
    smsContent,
    keywords,
    dateCategory,
    dateFrom,
    dateTo,
    countryId,
    walletBalance,
    ticketsPurchased,
    ticketPurchaseFrequency,
    daysSinceLastTrip
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function getBulkNotificationStatus ({ scheduledJobId }) {
  return apiRequests.get('/v1/passenger-profile/bulk-notification/status/', {
    params: { scheduledJobId },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function createUnusedBalanceRefund ({ passengerProfileId, amount, refundFromTransactionLogIds, dateCreated }) {
  return apiRequests.post('/v1/passenger-profile/' + passengerProfileId + '/unused-balance-refund/', {
    amount,
    refundFromTransactionLogIds,
    dateCreated
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function deleteUnusedBalanceRefund (id) {
  return apiRequests.delete('/v1/passenger-transaction-log/' + id + '/unused-balance-refund/', {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function createAgentCreditRefund ({
  passengerProfileId,
  amount,
  agentProfileId
}) {
  return apiRequests.post('/v1/passenger-profile/' + passengerProfileId + '/agent-credit-refund/', {
    amount,
    agentProfileId
  }, {
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

async function getPassengerRefundableBreakdown ({ passengerProfileId, phoneNumbers }) {
  return apiRequests.get('/v1/passenger-transaction-log/refundable-breakdown/', {
    params: { passengerProfileId, phoneNumbers },
    headers: { 'Authorization': store.getters.getTokenId }
  }).then((result) => {
    return result.data
  })
}

export {
  getArchivedOrUnarchivedPassenger,
  getPassengerIdentifier,
  findPassengerIdentifiers,
  findTotalPassengers,
  findPassengerIdentifiersByPassengerProfileId,
  findVerifiedPassengers,
  findTotalVerifiedPassengers,
  findPendingPassengers,
  findTotalPendingPassengers,
  findPendingTransferPassengers,
  findTotalPendingTransferPassengers,
  findArchivedPassengers,
  findTotalArchivedPassengers,
  archivePassenger,
  unarchivePassenger,
  findBulkPassengers,
  findTotalBulkPassengers,
  sendPassengersBulkNotification,
  getBulkNotificationStatus,
  sendNewPassengerPassword,
  clearPassengerReRegistrationGuard,
  refreshMobileMoneyProfile,
  createUnusedBalanceRefund,
  deleteUnusedBalanceRefund,
  createAgentCreditRefund,
  getPassengerRefundableBreakdown
}
